import { useMatchRoute } from '@tanstack/react-router';
import * as React from 'react';
import styled, { css } from 'styled-components';

import { typographyCss } from '@creditornot/cb-ingredients';
import { MenuItemGroup, MenuLinkItem } from '@creditornot/cb-menu';
import { Tag } from '@creditornot/cb-tag';
import { ReactRouterMenuLinkItem } from 'common/components/cookbook/ReactRouterMenuLinkItem';

import { Link, useTenantNavigationLinks } from './useTenantNavigationLinks';

const NavigationLinkSection = styled.div`
  margin-top: 24px;
  padding-left: 8px;
  padding-right: 8px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const GroupList = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
`;

const StyledLi = styled.li`
  margin-inline-start: -8px;
  width: calc(100% + 16px);
  margin-top: 4px;
`;

const StyledMenuItemGroup = styled(MenuItemGroup)`
  a + a {
    margin-top: 4px;
  }
`;

const StyledBadge = styled(Tag)<{ $isActive: boolean }>`
  display: flex;

  ${({ $isActive }) =>
    $isActive &&
    css`
      background-color: transparent;

      span {
        ${typographyCss.caption}
        font-weight: 700;
      }
    `}
`;

type Props = {
  onClick?: () => void;
};

export const NavigationLinks = ({ onClick }: Props): React.ReactElement => {
  const match = useMatchRoute();
  const groups = useTenantNavigationLinks();

  return (
    <nav>
      <NavigationLinkSection>
        {groups
          .filter((group) => group.links.length > 0)
          .map((group) => (
            <StyledMenuItemGroup key={group.name} title={group.name} role="group">
              <GroupList>
                {group.links.map(({ name, ...rest }: Link) => {
                  if ('url' in rest) {
                    const isActive = !!match({ to: rest.url, fuzzy: true });
                    return (
                      <StyledLi key={name}>
                        <MenuLinkItem
                          data-test-id={name}
                          key={name}
                          onClick={onClick}
                          href={rest.url}
                          role="link"
                          target="_blank"
                          active={isActive}
                        >
                          {name}
                        </MenuLinkItem>
                      </StyledLi>
                    );
                  }

                  const isActive = !!match({ to: rest.to, fuzzy: true });

                  return (
                    <StyledLi key={name}>
                      <ReactRouterMenuLinkItem
                        {...rest}
                        data-test-id={name}
                        key={name}
                        active={isActive}
                        to={rest.to}
                        role="link"
                        onClick={onClick}
                        rightContent={
                          rest.badge && (
                            <StyledBadge
                              variant="red"
                              size="tiny"
                              $isActive={isActive}
                              data-test-id={`menu-badge-${name.toLowerCase()}`}
                            >
                              <span>{rest.badge}</span>
                            </StyledBadge>
                          )
                        }
                      >
                        {name}
                      </ReactRouterMenuLinkItem>
                    </StyledLi>
                  );
                })}
              </GroupList>
            </StyledMenuItemGroup>
          ))}
      </NavigationLinkSection>
    </nav>
  );
};
